import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useCustomers } from "@/api/customers";
import {
  type AdminCarrierCustomerConnection,
  usePatchAdminCarrierCustomer,
} from "@/api/customer-carrier-discounts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

type Props = {
  carrierId: string;
  carrierName: string;
  connections: AdminCarrierCustomerConnection[];
};

export function GrantCustomerAccess(props: Props) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button onClick={() => setOpen(true)}>Grant customer access</Button>
      {open && <GrantDialog {...props} onClose={() => setOpen(false)} />}
    </>
  );
}

function GrantDialog({
  carrierId,
  carrierName,
  connections,
  onClose,
}: Props & { onClose: () => void }) {
  const [searchDraft, setSearchDraft] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState("");
  const customers = useCustomers({ search, page, limit: 10 });
  const grant = usePatchAdminCarrierCustomer();
  const selected = customers.data?.data.find(
    (customer) => customer.id === selectedId
  );
  const alreadyConnected = connections.some(
    (connection) => connection.customerId === selectedId
  );
  const message =
    grant.error && axios.isAxiosError<{ message?: string }>(grant.error)
      ? grant.error.response?.data?.message
      : null;

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !grant.isPending) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Grant customer access to {carrierName}</DialogTitle>
          <DialogDescription>
            Select a customer to grant EZX access. Carrier blocks and customer
            opt-outs remain in effect. Manage existing connections in the
            customer list.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-2"
          onSubmit={(event) => {
            event.preventDefault();
            setSelectedId("");
            setPage(1);
            setSearch(searchDraft.trim());
          }}
        >
          <Label htmlFor="grant-customer-search">Search customers</Label>
          <div className="flex gap-2">
            <Input
              id="grant-customer-search"
              value={searchDraft}
              onChange={(event) => setSearchDraft(event.target.value)}
              placeholder="Name or email"
              disabled={grant.isPending}
            />
            <Button type="submit" variant="outline" disabled={grant.isPending}>
              Search
            </Button>
          </div>
        </form>
        {customers.isError ? (
          <div role="alert" className="text-sm text-destructive">
            Could not load customers.
            <Button
              variant="link"
              disabled={customers.isCoolingDown || customers.isFetching}
              onClick={() => void customers.refetch()}
            >
              Retry
            </Button>
          </div>
        ) : customers.isFetching ? (
          <p role="status">Loading customers…</p>
        ) : (
          <fieldset
            className="max-h-64 space-y-2 overflow-y-auto"
            disabled={grant.isPending}
          >
            <legend className="sr-only">Customer to grant access</legend>
            {customers.data?.data.map((customer) => {
              const connected = connections.some(
                (connection) => connection.customerId === customer.id
              );
              const name =
                [customer.firstName, customer.lastName]
                  .filter(Boolean)
                  .join(" ") ||
                customer.email ||
                customer.id;
              return (
                <label
                  key={customer.id}
                  className="flex items-start gap-3 rounded-md border p-3 text-sm"
                >
                  <input
                    type="radio"
                    name="grant-customer"
                    value={customer.id}
                    checked={selectedId === customer.id}
                    disabled={connected}
                    onChange={() => {
                      setSelectedId(customer.id);
                      grant.reset();
                    }}
                  />
                  <span>
                    <span className="block font-medium">{name}</span>
                    <span className="block text-muted-foreground">
                      {customer.email}
                    </span>
                    {connected && (
                      <span className="block text-muted-foreground">
                        Already connected — manage access in the customer list.
                      </span>
                    )}
                  </span>
                </label>
              );
            })}
            {customers.data?.data.length === 0 && <p>No customers found.</p>}
          </fieldset>
        )}
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            disabled={
              page === 1 ||
              customers.isFetching ||
              customers.isCoolingDown ||
              grant.isPending
            }
            onClick={() => {
              setSelectedId("");
              setPage(page - 1);
            }}
          >
            Previous
          </Button>
          <span className="text-sm">Page {page}</span>
          <Button
            variant="outline"
            disabled={
              !customers.data?.pagination.hasNextPage ||
              !!customers.error ||
              customers.isCoolingDown ||
              customers.isFetching ||
              grant.isPending
            }
            onClick={() => {
              setSelectedId("");
              setPage(page + 1);
            }}
          >
            Next
          </Button>
        </div>
        {grant.isError && (
          <p role="alert" className="text-sm text-destructive">
            {message || "Could not grant access. Try again."}
          </p>
        )}
        <DialogFooter>
          <Button
            variant="outline"
            disabled={grant.isPending}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            disabled={
              !selected ||
              alreadyConnected ||
              customers.isFetching ||
              customers.isError ||
              grant.isPending
            }
            onClick={() => {
              if (!selected) return;
              grant.mutate(
                {
                  carrierId,
                  customerId: selected.id,
                  adminBlock: { blocked: false },
                },
                {
                  onSuccess: () => {
                    toast.success("EZX access granted");
                    onClose();
                  },
                }
              );
            }}
          >
            {grant.isPending ? "Granting…" : "Grant access"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
